import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ButtonCard from '@narvar/nth-button-card';
import TextLink from '@narvar/nth-text-link';
import styled from 'styled-components';
import OptionIcon from './OptionIcon';
import Icon from '../Icon';
import ReturnCardContainer from '../ReturnCardContainer';
import { a11ySelection } from '../../lib/a11yUtils';
import DEFAULT_COPY from '../../constants/defaultCopy';

const ReturnCardIcon = styled.div`
  width: 100%;
  display: inline-block;
  text-align: center;
  padding: 1rem 1rem 0 1rem;
  @media only screen and (max-width: 768px) {
    width: 8rem;
    vertical-align: top;
  }
`;

const ReturnCardContent = styled.div`
  width: 100%;
  display: inline-block;
  text-align: center;
  padding: 1rem;
  @media only screen and (max-width: 768px) {
    width: calc(100% - 8rem);
    text-align: left;
    padding: 1rem 1rem 1rem 0;
  }
`;

const ReturnOptionButton = styled.div`
  min-height: ${(props) => (props.optionsCount === 1 ? '18rem' : '22rem')};
  width: 100%;
  @media only screen and (max-width: 768px) {
    min-height: 10rem;
  }
`;

export const mapStateToProps = (
  {
    language = {},
    retailerSettings: {
      branding: {
        link_color: linkColor,
        link_hover_color: linkHoverColor,
        brand_call_to_action: brandCTAColor,
      } = {},
    } = {},
    page: { languageCode, productType } = {},
    returnsSettings: { [productType]: { returnOptions: { useWrappingLayout } = {} } = {} } = {},
  },
  { optionKey },
) => ({
  linkColor,
  linkHoverColor,
  brandCTAColor,
  language,
  languageCode,
  optionKey,
  useWrappingLayout,
});

const ReturnOptionsCard = ({
  onClick,
  selected,
  linkColor,
  linkHoverColor,
  brandCTAColor,
  optionsCount,
  fee,
  optionKey,
  language,
  languageCode,
  modal,
  useWrappingLayout,
  hideLink,
  preferred,
}) => {
  const { [languageCode]: DEFAULT_LANG_COPY = {} } = DEFAULT_COPY || {};
  const title =
    language[`returns.return_options.${optionKey}.title`] ||
    DEFAULT_LANG_COPY[`returns.return_options.${optionKey}.title`];
  const subtitle =
    language[`returns.return_options.${optionKey}.subtitle`] ||
    DEFAULT_LANG_COPY[`returns.return_options.${optionKey}.subtitle`];
  const description =
    language[`returns.return_options.${optionKey}.description`] ||
    DEFAULT_LANG_COPY[`returns.return_options.${optionKey}.description`];
  const storeLinkURL =
    language[`returns.return_options.${optionKey}.store_link`] ||
    DEFAULT_LANG_COPY[`returns.return_options.${optionKey}.store_link`];
  const storeLinkText =
    language[`returns.return_options.${optionKey}.store_link_text`] ||
    DEFAULT_LANG_COPY[`returns.return_options.${optionKey}.store_link_text`];

  const returnOptionCardWidth = (() => {
    if (!useWrappingLayout || optionsCount < 4) return `${100 / optionsCount - 2}%`;
    if (optionsCount === 4) return '48%';
    return '31.33%';
  })();

  return (
    <ReturnCardContainer
      s={{
        position: 'relative',
        display: useWrappingLayout || optionsCount === 1 ? 'inline-block' : 'table-cell',
        width: returnOptionCardWidth,
        hoverBorderColor: brandCTAColor,
        hoverBorderWidth: selected ? '3px' : '1px',
        borderWidth: selected ? '3px' : '1px',
        marginTop: '1%',
        marginRight: '1%',
        marginBottom: '1%',
        marginLeft: '1%',
        borderColor: selected ? `${brandCTAColor}` : '#ccc',
        ...(optionsCount === 1 && {
          hoverBorderStyle: 'none',
          hoverBackground: 'transparent',
          borderStyle: 'none',
          background: 'transparent',
          paddingTop: '0px',
          paddingBottom: '0px',
        }),
      }}
      onClick={a11ySelection(onClick)}
      onKeyDown={a11ySelection(onClick)}
      tabIndex="0"
      data-test={optionKey}
      role="button"
      className={`option-card-${optionKey}`}
    >
      {selected && <span className="sr-only">selected</span>}
      <ReturnOptionButton data-testid="return-option-button" optionsCount={optionsCount}>
        <ReturnCardIcon>
          <OptionIcon optionKey={optionKey} brandCTAColor={brandCTAColor} />
        </ReturnCardIcon>
        <ReturnCardContent>
          <ButtonCard.BigHeader
            className="title"
            s={{
              margin: '1rem auto',
              fontWeight: 'bold',
              fontSize: '1.25rem',
              lineHeight: '1.25rem',
            }}
            dangerouslySetInnerHTML={{ __html: title }}
          />
          {subtitle ? (
            <ButtonCard.SmallHeader
              className="subtitle"
              s={{ color: '#000000', fontWeight: 'bold', margin: '1rem auto' }}
              dangerouslySetInnerHTML={{ __html: subtitle }}
            />
          ) : (
            <React.Fragment />
          )}
          <ButtonCard.SmallHeader
            className="fee"
            s={{ color: '#000000', fontWeight: 'bold', margin: '1rem auto' }}
          >
            {fee}
          </ButtonCard.SmallHeader>
          <ButtonCard.Paragraph
            className="description"
            s={{ fontSize: '0.9rem' }}
            dangerouslySetInnerHTML={{ __html: description }}
          />
          {!hideLink && storeLinkText && storeLinkURL ? (
            <TextLink
              target="_blank"
              href={storeLinkURL}
              s={{ color: linkColor, hoverColor: linkHoverColor, textDecoration: 'underline' }}
            >
              {storeLinkText}
            </TextLink>
          ) : (
            <React.Fragment />
          )}
          {modal || <React.Fragment />}
        </ReturnCardContent>
      </ReturnOptionButton>
      {preferred && (
        /* eslint-disable-next-line react/jsx-no-target-blank */
        <a href={process.env.NARVAR_PROFILE_URL} target="_blank">
          <Icon.Heart
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
            }}
            width="16px"
            fill={selected ? brandCTAColor : '#ccc'}
          />
        </a>
      )}
    </ReturnCardContainer>
  );
};

ReturnOptionsCard.defaultProps = {
  linkColor: '#2b7cac',
  linkHoverColor: '#2b7cac',
  brandCTAColor: '#000000',
  fee: '',
  optionsCount: 1,
  optionKey: '',
  modal: null,
  useWrappingLayout: false,
  hideLink: false,
  preferred: false,
};

ReturnOptionsCard.propTypes = {
  onClick: PropTypes.func.isRequired,
  selected: PropTypes.bool.isRequired,
  linkColor: PropTypes.string,
  linkHoverColor: PropTypes.string,
  brandCTAColor: PropTypes.string,
  optionsCount: PropTypes.number,
  fee: PropTypes.string,
  optionKey: PropTypes.string,
  language: PropTypes.object.isRequired,
  languageCode: PropTypes.string.isRequired,
  modal: PropTypes.node,
  useWrappingLayout: PropTypes.bool,
  hideLink: PropTypes.bool,
  preferred: PropTypes.bool,
};

export default connect(mapStateToProps)(ReturnOptionsCard);
